import {
	Dispatch,
	FC,
	ForwardedRef,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import {
	convertProctoringAssessmentConfiguration,
	convertProctoringOption,
	EditQuizPage,
	EditQuizRef,
} from "./editQuiz";
import { api as defaultApi, VerityApi } from "../api/fetch";
// import EnableVerityCheckbox from "./enableVeritySwitch";
import ThemeWrapper from "../theme";
import { createPortal } from "react-dom";
import { AssessmentInfo } from "@verity/api-client/types";
import password from "./password";
import { CoreSpinner } from "../core";
import {
	ProctoringOption,
	ProctoringAssessmentConfigurationV2,
	ProctoringOptionProfileV2,
} from "@verity/proctoring-option/types";
import { VerityLogger } from "@yujacorp/verity-logger";
import { DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION, DEFAULT_PROCTORING_OPTION } from "@verity/proctoring-option";
import { ExemptedStudent, VerityInstituteSettings } from "..";
import { ConfigurationPageName } from "./proctoring-options/option-text";

export type EditQuizWrapperProps = {
	quizName?: string;
	api?: VerityApi;
	switchComponent?: EditQuizSwitchComponent;
	quizInfo: AssessmentInfo;
	portalElement?: HTMLElement;
	onStartSaveOptions?: () => void;
	onSaveOptions?: (
		success: boolean,
		messages?: string[],
		options?: Parameters<VerityApi["saveProctoringAssessmentConfiguration"]>[0],
	) => void;
	onInitialOptions?: (
		proctoringAssessmentConfiguration: Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">,
		proctoringOption: Partial<ProctoringOption>,
	) => void;
	onEnabledChange: (
		proctoringAssessmentConfiguration: Pick<Partial<ProctoringAssessmentConfigurationV2>, "enabled" | "password">,
		onPasswordGenerated: () => void,
	) => void | Promise<void>;
	onBackEnabledChange?: (enabled: boolean) => void;
	onNextEnabledChange?: (enabled: boolean) => void;
	onSaveEnabledChange?: (enabled: boolean) => void;
	onClose: () => void;
	setIsFinalPage?: (value: boolean) => void;
	forceThirdParty?: boolean;
	uploadedExemptedStudentsMap: Map<number, ExemptedStudent>;
	setUploadedExemptedStudentsMap: React.Dispatch<React.SetStateAction<Map<number, ExemptedStudent>>>;
	uploadedProfilesMap: Map<number, ProctoringOptionProfileV2>;
	setUploadedProfilesMap: React.Dispatch<React.SetStateAction<Map<number, ProctoringOptionProfileV2>>>;
	isFirstTimeThirdPartyCreation: boolean;
	logger?: VerityLogger;

	step?: number;
	setStep?: Dispatch<SetStateAction<number>>;
	totalSteps?: { name: string; label: string }[];
};

export type EditQuizWrapperRef = {
	saveButtonHandler: (password?: string, additionalQuizInfo?: Partial<AssessmentInfo>) => Promise<boolean>;
	closeButtonHandler: () => void;
	saveAsProfile: () => void;
	setEnabled: (enabled: boolean) => void;
	nextButtonHandler: () => void;
	backButtonHandler: () => void;
	goToPage: (pageName: ConfigurationPageName) => void;
};

export type EditQuizSwitchComponent = FC<{
	enabled: boolean;
	setEnabled: (_: boolean) => void;
}>;

export function getDefaultProctoringOption(verityInstituteSettings: VerityInstituteSettings) {
	return {
		...DEFAULT_PROCTORING_OPTION,
		isExtension: verityInstituteSettings.allowExtensionPlatform,
		isLockdown: verityInstituteSettings.allowExtensionPlatform ? false : verityInstituteSettings.allowLockdownPlatform,
		isMobile:
			verityInstituteSettings.allowExtensionPlatform || verityInstituteSettings.allowLockdownPlatform
				? false
				: verityInstituteSettings.allowMobilePlatform,
	};
}

export const EditQuizWrapper = forwardRef(function EditQuizWrapper(
	{
		quizName,
		api = defaultApi,
		quizInfo,
		portalElement,
		onStartSaveOptions,
		onSaveOptions,
		onInitialOptions,
		onEnabledChange,
		onBackEnabledChange,
		onNextEnabledChange,
		onSaveEnabledChange,
		onClose,
		setIsFinalPage,
		forceThirdParty = false,
		uploadedExemptedStudentsMap,
		setUploadedExemptedStudentsMap,
		uploadedProfilesMap,
		setUploadedProfilesMap,
		isFirstTimeThirdPartyCreation,
		logger,
		step,
		setStep,
		totalSteps,
	}: EditQuizWrapperProps,
	ref: ForwardedRef<EditQuizWrapperRef>,
) {
	const {
		getProctoringAssessmentConfiguration,
		areProctoringProfilesEnforced,
		getDefaultProctoringProfile,
		getVerityInstituteSettings,
		getUserInstituteId,
	} = api;

	const [initialProctoringAssessmentConfiguration, setInitialProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [proctoringAssessmentConfiguration, setProctoringAssessmentConfiguration] =
		useState<Omit<Partial<ProctoringAssessmentConfigurationV2>, "proctoringOptionProfile">>();
	const [initialProctoringOption, setInitialProctoringOption] = useState<Partial<ProctoringOption>>();
	const [proctoringOption, setProctoringOption] = useState<Partial<ProctoringOption>>();
	const [currentProfileId, setCurrentProfileId] = useState<number>();
	const [enforcedProfilesOnly, setEnforcedProfilesOnly] = useState<boolean>(true);

	const editQuizPageRef = useRef<EditQuizRef>(null);

	const saveButtonHandler: EditQuizWrapperRef["saveButtonHandler"] = async (password, additionalQuizInfo) =>
		(await editQuizPageRef.current?.saveButtonHandler(password, additionalQuizInfo)) ?? false;

	const closeButtonHandler: EditQuizWrapperRef["closeButtonHandler"] = () => {
		editQuizPageRef.current?.closeButtonHandler();
	};

	const setEnabled: EditQuizWrapperRef["setEnabled"] = async (enabled) => {
		if (!proctoringAssessmentConfiguration || !quizInfo.courseId) return;
		proctoringAssessmentConfiguration.enabled = enabled;
		const instituteId = await getUserInstituteId();
		proctoringAssessmentConfiguration.password =
			proctoringAssessmentConfiguration.password ||
			password.generateKey(instituteId, quizInfo.courseId, quizInfo.quizId);
		try {
			if (proctoringAssessmentConfiguration) {
				setProctoringAssessmentConfiguration({ ...proctoringAssessmentConfiguration });
			}
			await onEnabledChange(proctoringAssessmentConfiguration, () => undefined);
		} catch (err) {
			console.error(err);
			alert("Something went wrong.");
		}
	};

	const saveAsProfile: EditQuizWrapperRef["saveAsProfile"] = () => {
		editQuizPageRef.current?.saveAsProfileButtonHandler();
	};

	const nextButtonHandler: EditQuizWrapperRef["nextButtonHandler"] = () => {
		editQuizPageRef.current?.nextButtonHandler();
	};

	const backButtonHandler: EditQuizWrapperRef["backButtonHandler"] = () => {
		editQuizPageRef.current?.backButtonHandler();
	};

	const goToPage: EditQuizWrapperRef["goToPage"] = (pageName: ConfigurationPageName) => {
		editQuizPageRef.current?.goToPage(pageName);
	};

	const [defaultProctoringOption, setDefaultProctoringOption] = useState<ProctoringOption | null>(null);

	useImperativeHandle(ref, () => ({
		saveButtonHandler,
		closeButtonHandler,
		setEnabled,
		saveAsProfile,
		nextButtonHandler,
		backButtonHandler,
		goToPage,
	}));

	useEffect(() => {
		void (async () => {
			setEnforcedProfilesOnly(await areProctoringProfilesEnforced());
			const defaultProfile = await getDefaultProctoringProfile();
			const verityInstituteSettings = await getVerityInstituteSettings();
			const defaultProctoringOption = getDefaultProctoringOption(verityInstituteSettings);
			setDefaultProctoringOption(defaultProctoringOption);
			const defaultInitialProctoringOption = defaultProfile?.proctoringOption ?? defaultProctoringOption;
			const getProctoringAssessmentConfigurationResp = await getProctoringAssessmentConfiguration(quizInfo);
			if (getProctoringAssessmentConfigurationResp) {
				const { proctoringOptionProfile, ...configuration } = getProctoringAssessmentConfigurationResp;
				const convertedConfiguration = convertProctoringAssessmentConfiguration(configuration, forceThirdParty);
				setInitialProctoringAssessmentConfiguration({ ...convertedConfiguration });
				setProctoringAssessmentConfiguration(convertedConfiguration);
				setInitialProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
				setProctoringOption(convertProctoringOption(proctoringOptionProfile.proctoringOption));
				setCurrentProfileId(proctoringOptionProfile.id);
				onInitialOptions?.(configuration, proctoringOptionProfile.proctoringOption);
			} else {
				const convertedConfiguration = convertProctoringAssessmentConfiguration(
					{ ...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION, enabled: false },
					forceThirdParty,
				);
				setInitialProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? { ...convertedConfiguration });
				setProctoringAssessmentConfiguration(proctoringAssessmentConfiguration ?? convertedConfiguration);
				setInitialProctoringOption(initialProctoringOption ?? defaultInitialProctoringOption);
				setProctoringOption(initialProctoringOption ?? defaultInitialProctoringOption);
				setCurrentProfileId(defaultProfile?.id);
				onInitialOptions?.({}, initialProctoringOption ?? defaultInitialProctoringOption);
			}
		})();
	}, []);

	let optionsPage;
	optionsPage = (
		<div style={{ display: proctoringAssessmentConfiguration?.enabled ? undefined : "none" }}>
			<ThemeWrapper>
				<EditQuizPage
					quizName={quizName}
					ref={editQuizPageRef}
					api={api}
					quizInfo={quizInfo}
					initialProctoringAssessmentConfiguration={initialProctoringAssessmentConfiguration}
					proctoringAssessmentConfiguration={proctoringAssessmentConfiguration}
					setProctoringAssessmentConfiguration={setProctoringAssessmentConfiguration}
					initialProctoringOption={initialProctoringOption}
					proctoringOption={proctoringOption}
					setProctoringOption={setProctoringOption}
					currentProfileId={currentProfileId}
					setCurrentProfileId={setCurrentProfileId}
					enforcedProfilesOnly={enforcedProfilesOnly}
					onBackEnabledChange={onBackEnabledChange}
					onNextEnabledChange={onNextEnabledChange}
					onSaveEnabledChange={onSaveEnabledChange}
					onClose={onClose}
					setIsFinalPage={setIsFinalPage}
					forceStartOnFirstPage={isFirstTimeThirdPartyCreation}
					onStartSaveOptions={onStartSaveOptions}
					onSaveOptions={onSaveOptions ?? (() => null)}
					uploadedExemptedStudentsMap={uploadedExemptedStudentsMap}
					setUploadedExemptedStudentsMap={setUploadedExemptedStudentsMap}
					uploadedProfilesMap={uploadedProfilesMap}
					setUploadedProfilesMap={setUploadedProfilesMap}
					logger={logger}
					defaultProctoringOption={defaultProctoringOption}
					step={step}
					setStep={setStep}
					totalSteps={totalSteps}
				/>
			</ThemeWrapper>
		</div>
	);
	if (portalElement) {
		optionsPage = createPortal(optionsPage, portalElement);
	}
	//Do Not Remove this Switch for now becasue in case third party need to enable and disable verity
	// const Switch = switchComponent ?? EnableVerityCheckbox;

	return (
		<div id="root">
			{proctoringAssessmentConfiguration ? (
				<ThemeWrapper>
					<div style={{ position: "relative" }}>
						{/* <Switch enabled={Boolean(proctoringAssessmentConfiguration.enabled)} setEnabled={setEnabled} /> */}
					</div>
				</ThemeWrapper>
			) : (
				<div
					style={{
						height: "512px",
						display: "flex",
						placeContent: "center",
						alignItems: "center",
						flexDirection: "column",
					}}
				>
					<CoreSpinner size={100} />
				</div>
			)}
			{optionsPage}
		</div>
	);
});
